<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-blue-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-blue-600 tracking-widest">Catálogo Bancario</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Bancos
          <span class="px-2 sm:px-3 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-xs sm:text-sm font-black rounded-lg border border-blue-200 dark:border-blue-800">
            {{ bancos.length }}
          </span>
        </h1>
      </div>
      <div class="flex items-center gap-2 w-full sm:w-auto flex-wrap justify-end">
        <button @click="loadBancos" class="px-3 sm:px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest hover:bg-gray-200 transition-all flex items-center gap-1">
          <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" />
          <span class="hidden sm:inline">Actualizar</span>
        </button>
        <button @click="openCreateModal" class="px-3 sm:px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-md flex items-center gap-1 sm:gap-2">
          <Icon icon="mdi:plus-circle" class="text-base sm:text-lg" />
          <span class="hidden sm:inline">Nuevo Banco</span>
          <span class="sm:hidden">Nuevo</span>
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5 animate-fadeInUp">
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
        <div class="sm:col-span-6">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Buscar</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-blue-500" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Código, nombre..."
              class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-blue-500 rounded-xl transition-all outline-none dark:text-white font-bold text-sm"
            />
          </div>
        </div>
        <div class="sm:col-span-3">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Pago Móvil</label>
          <select v-model="filterPagoMovil" class="w-full px-3 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-blue-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm">
            <option :value="null">TODOS</option>
            <option :value="true">Solo Pago Móvil</option>
            <option :value="false">No Pago Móvil</option>
          </select>
        </div>
        <div class="sm:col-span-3">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Estado</label>
          <select v-model="filterEstado" class="w-full px-3 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-blue-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm">
            <option value="all">TODOS</option>
            <option value="activo">ACTIVOS</option>
            <option value="inactivo">INACTIVOS</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-5 animate-fadeInUp delay-100">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Total Bancos</p>
            <p class="text-xl sm:text-2xl font-black text-gray-900 dark:text-white">{{ bancos.length }}</p>
          </div>
          <div class="p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
            <Icon icon="mdi:bank" class="text-blue-600 text-xl sm:text-2xl" />
          </div>
        </div>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Activos</p>
            <p class="text-xl sm:text-2xl font-black text-emerald-600">{{ estadisticas.activos }}</p>
          </div>
          <div class="p-2 sm:p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl">
            <Icon icon="mdi:check-decagram" class="text-emerald-600 text-xl sm:text-2xl" />
          </div>
        </div>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Pago Móvil</p>
            <p class="text-xl sm:text-2xl font-black text-indigo-600">{{ estadisticas.pagoMovil }}</p>
          </div>
          <div class="p-2 sm:p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl">
            <Icon icon="mdi:cellphone" class="text-indigo-600 text-xl sm:text-2xl" />
          </div>
        </div>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Cuentas</p>
            <p class="text-xl sm:text-2xl font-black text-purple-600">{{ estadisticas.totalCuentas }}</p>
          </div>
          <div class="p-2 sm:p-3 bg-purple-50 dark:bg-purple-900/30 rounded-xl">
            <Icon icon="mdi:credit-card-multiple" class="text-purple-600 text-xl sm:text-2xl" />
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden animate-fadeInUp delay-200">
      <div class="hidden lg:block overflow-x-auto custom-scrollbar">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Código</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Banco</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Pago Móvil</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Cuentas</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Estado</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="loading" v-for="i in 5" :key="'load-'+i" class="animate-pulse">
              <td colspan="6" class="px-5 py-4"><div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full"></div></td>
            </tr>
            <tr v-if="!loading && filteredBancos.length === 0">
              <td colspan="6" class="px-5 py-16 text-center">
                <div class="flex flex-col items-center opacity-30 select-none">
                  <Icon icon="mdi:bank-off" class="text-7xl mb-3" />
                  <h4 class="text-xl font-black text-gray-400 tracking-widest">Sin bancos</h4>
                </div>
              </td>
            </tr>
            <tr v-for="banco in paginatedBancos" :key="banco.idBanco" class="hover:bg-blue-50/30 dark:hover:bg-gray-700/30 transition-all">
              <td class="px-5 py-4">
                <span class="px-3 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-lg font-black text-xs">{{ banco.codigo }}</span>
              </td>
              <td class="px-5 py-4">
                <div class="text-sm font-black text-gray-900 dark:text-white">{{ banco.nombre }}</div>
                <div class="text-[10px] text-gray-500">{{ banco.nombreCorto || '—' }}</div>
              </td>
              <td class="px-5 py-4 text-center">
                <span :class="[
                  'px-3 py-1 inline-flex text-[10px] font-black uppercase tracking-widest rounded-lg border',
                  banco.esPagoMovil
                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800'
                    : 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
                ]">
                  <Icon :icon="banco.esPagoMovil ? 'mdi:check' : 'mdi:close'" class="mr-1" />
                  {{ banco.esPagoMovil ? 'Sí' : 'No' }}
                </span>
              </td>
              <td class="px-5 py-4 text-center">
                <span class="px-3 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-lg font-black text-xs">{{ banco.totalCuentas || 0 }}</span>
              </td>
              <td class="px-5 py-4 text-center">
                <span :class="[
                  'px-3 py-1 inline-flex text-[10px] font-black uppercase tracking-widest rounded-lg border',
                  banco.estado === 'activo'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-rose-50 text-rose-700 border-rose-200'
                ]">{{ banco.estado }}</span>
              </td>
              <td class="px-5 py-4 text-right">
                <div class="flex items-center justify-end gap-1">
                  <button @click="openEditModal(banco)" class="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 rounded-lg hover:bg-amber-600 hover:text-white transition-all">
                    <Icon icon="mdi:pencil" class="text-base" />
                  </button>
                  <button @click="abrirModalEliminar(banco)" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 rounded-lg hover:bg-rose-600 hover:text-white transition-all">
                    <Icon icon="mdi:delete" class="text-base" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile cards -->
      <div class="lg:hidden space-y-3 p-2">
        <div v-for="banco in paginatedBancos" :key="banco.idBanco" class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4">
          <div class="flex items-center justify-between mb-2">
            <span class="px-3 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-lg font-black text-sm">{{ banco.codigo }}</span>
            <span :class="['px-2 py-0.5 text-[8px] font-black uppercase rounded-lg', banco.estado === 'activo' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700']">{{ banco.estado }}</span>
          </div>
          <p class="text-base font-black text-gray-900 dark:text-white">{{ banco.nombre }}</p>
          <p class="text-xs text-gray-500 mb-2">{{ banco.nombreCorto || '—' }}</p>
          <div class="flex items-center gap-2 mb-3 text-[10px]">
            <span v-if="banco.esPagoMovil" class="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-lg font-black">PAGO MÓVIL</span>
            <span class="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-lg font-black">{{ banco.totalCuentas || 0 }} CUENTAS</span>
          </div>
          <div class="flex gap-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button @click="openEditModal(banco)" class="flex-1 p-2 bg-amber-50 dark:bg-amber-900/30 text-amber-600 rounded-lg hover:bg-amber-600 hover:text-white transition-all flex items-center justify-center gap-1 text-[10px] font-black">
              <Icon icon="mdi:pencil" /> Editar
            </button>
            <button @click="abrirModalEliminar(banco)" class="flex-1 p-2 bg-rose-50 dark:bg-rose-900/30 text-rose-600 rounded-lg hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center gap-1 text-[10px] font-black">
              <Icon icon="mdi:delete" /> Eliminar
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="filteredBancos.length > 0 && !loading" class="bg-gray-50/50 dark:bg-gray-800/50 px-3 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div class="text-[10px] font-black text-gray-400 uppercase">Mostrando {{ startItem }} - {{ endItem }} de {{ filteredBancos.length }}</div>
        <div class="flex items-center gap-1">
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1" class="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 disabled:opacity-30">
            <Icon icon="mdi:chevron-left" />
          </button>
          <span class="text-xs font-black text-gray-600 dark:text-gray-300 px-2">{{ currentPage }} / {{ totalPages }}</span>
          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages" class="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 disabled:opacity-30">
            <Icon icon="mdi:chevron-right" />
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL FORMULARIO -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]" @click.self="showModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 sm:px-6 py-4 flex items-center gap-3">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editingBanco ? 'mdi:pencil-box' : 'mdi:bank-plus'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-sm font-black text-white">{{ editingBanco ? 'Editar Banco' : 'Nuevo Banco' }}</h3>
              <p class="text-[10px] text-white/70 font-bold">{{ editingBanco ? 'Modifica los datos' : 'Completa los datos' }}</p>
            </div>
            <button @click="showModal = false" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg">
              <Icon icon="mdi:close" class="text-white" />
            </button>
          </div>
          <div class="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <form @submit.prevent="saveBanco" class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Código *</label>
                  <input v-model="form.codigo" required maxlength="4" class="w-full px-3 py-2.5 bg-white dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-black dark:text-white outline-none" placeholder="0134" />
                </div>
                <div class="sm:col-span-2">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Nombre *</label>
                  <input v-model="form.nombre" required maxlength="100" class="w-full px-3 py-2.5 bg-white dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-bold dark:text-white outline-none" placeholder="Banesco" />
                </div>
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Nombre Corto</label>
                <input v-model="form.nombreCorto" maxlength="50" class="w-full px-3 py-2.5 bg-white dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm dark:text-white outline-none" />
              </div>
              <div class="flex items-center gap-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl">
                <input id="pagoMovil" v-model="form.esPagoMovil" type="checkbox" class="w-5 h-5 text-indigo-600 rounded" />
                <label for="pagoMovil" class="text-sm font-black text-indigo-700 dark:text-indigo-300 flex items-center gap-2">
                  <Icon icon="mdi:cellphone" class="text-lg" />
                  Participa en Pago Móvil
                </label>
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Estado</label>
                <div class="flex gap-4">
                  <label class="flex items-center gap-2 text-xs font-bold"><input type="radio" v-model="form.estado" value="activo" /> Activo</label>
                  <label class="flex items-center gap-2 text-xs font-bold"><input type="radio" v-model="form.estado" value="inactivo" /> Inactivo</label>
                </div>
              </div>
              <div class="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" @click="showModal = false" class="w-full sm:flex-1 py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-black text-xs tracking-widest">Cancelar</button>
                <button type="submit" :disabled="saving" class="w-full sm:flex-[2] py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                  <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': saving}" />
                  {{ saving ? 'Guardando...' : (editingBanco ? 'Actualizar' : 'Crear Banco') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL CONFIRMACIÓN -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10002]" @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <div class="px-4 sm:px-6 py-4 bg-gradient-to-r from-red-600 to-pink-600">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl">
                <Icon icon="mdi:alert-circle" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-sm font-black text-white">Eliminar Banco</h3>
                <p class="text-[10px] text-white/80">Esta acción no se puede deshacer</p>
              </div>
            </div>
          </div>
          <div class="p-4 sm:p-6">
            <p class="text-sm text-gray-700 dark:text-gray-300">¿Eliminar el banco <strong>{{ confirmModalBanco?.nombre }}</strong>?</p>
          </div>
          <div class="px-4 sm:px-6 py-4 bg-gray-50 dark:bg-gray-900 flex gap-3">
            <button @click="cancelarConfirmacion" class="flex-1 py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-black text-xs">Cancelar</button>
            <button @click="ejecutarEliminar" :disabled="confirmModalCargando" class="flex-[2] py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-xl font-black text-xs uppercase flex items-center justify-center gap-2">
              <Icon :icon="confirmModalCargando ? 'mdi:loading' : 'mdi:check'" :class="{'animate-spin': confirmModalCargando}" />
              {{ confirmModalCargando ? 'Eliminando...' : 'Eliminar' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import {
  obtenerBancos, crearBanco, actualizarBanco, eliminarBanco
} from '@/api/bancos.js';

export default {
  name: 'BancosList',
  components: { Icon },
  data() {
    return {
      bancos: [],
      loading: false,
      saving: false,
      showModal: false,
      searchQuery: '',
      filterPagoMovil: null,
      filterEstado: 'all',
      currentPage: 1,
      itemsPerPage: 10,
      editingBanco: null,
      form: {
        idBanco: null, codigo: '', nombre: '', nombreCorto: '',
        esPagoMovil: true, estado: 'activo'
      },
      // confirm
      showConfirmModal: false,
      confirmModalBanco: null,
      confirmModalCargando: false
    };
  },
  computed: {
    filteredBancos() {
      let filtered = [...this.bancos];
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(b =>
          b.codigo?.toLowerCase().includes(q) ||
          b.nombre?.toLowerCase().includes(q) ||
          b.nombreCorto?.toLowerCase().includes(q)
        );
      }
      if (this.filterPagoMovil !== null) {
        filtered = filtered.filter(b => b.esPagoMovil === this.filterPagoMovil);
      }
      if (this.filterEstado !== 'all') {
        filtered = filtered.filter(b => b.estado === this.filterEstado);
      }
      return filtered;
    },
    estadisticas() {
      return {
        activos: this.bancos.filter(b => b.estado === 'activo').length,
        pagoMovil: this.bancos.filter(b => b.esPagoMovil).length,
        totalCuentas: this.bancos.reduce((s, b) => s + (b.totalCuentas || 0), 0)
      };
    },
    paginatedBancos() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredBancos.slice(start, start + this.itemsPerPage);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredBancos.length / this.itemsPerPage));
    },
    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredBancos.length);
    }
  },
  mounted() { this.loadBancos(); },
  watch: {
    filterPagoMovil() { this.currentPage = 1; },
    filterEstado() { this.currentPage = 1; },
    searchQuery() { this.currentPage = 1; }
  },
  methods: {
    async loadBancos() {
      this.loading = true;
      try {
        const res = await obtenerBancos();
        if (res.success) this.bancos = res.data;
      } finally {
        this.loading = false;
      }
    },
    openCreateModal() {
      this.editingBanco = null;
      this.form = { idBanco: null, codigo: '', nombre: '', nombreCorto: '', esPagoMovil: true, estado: 'activo' };
      this.showModal = true;
    },
    openEditModal(banco) {
      this.editingBanco = banco;
      this.form = {
        idBanco: banco.idBanco,
        codigo: banco.codigo,
        nombre: banco.nombre,
        nombreCorto: banco.nombreCorto,
        esPagoMovil: banco.esPagoMovil,
        estado: banco.estado
      };
      this.showModal = true;
    },
    async saveBanco() {
      this.saving = true;
      try {
        const res = this.editingBanco
          ? await actualizarBanco(this.editingBanco.idBanco, this.form)
          : await crearBanco(this.form);
        if (res.success) {
          await this.loadBancos();
          this.showModal = false;
        }
      } finally {
        this.saving = false;
      }
    },
    abrirModalEliminar(banco) {
      this.confirmModalBanco = banco;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmModalBanco = null;
    },
    async ejecutarEliminar() {
      this.confirmModalCargando = true;
      try {
        const res = await eliminarBanco(this.confirmModalBanco.idBanco);
        if (res.success) {
          await this.loadBancos();
          this.cancelarConfirmacion();
        }
      } finally {
        this.confirmModalCargando = false;
      }
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
@keyframes zoomIn { from { opacity:0; transform:scale(.95);} to {opacity:1; transform:scale(1);} }
.animate-zoomIn { animation: zoomIn .2s ease-out; }
@keyframes fadeInUp { from {opacity:0; transform:translateY(15px);} to {opacity:1; transform:translateY(0);} }
.animate-fadeInUp { animation: fadeInUp .4s ease-out forwards; }
@keyframes fadeIn { from {opacity:0;} to {opacity:1;} }
.animate-fadeIn { animation: fadeIn .3s ease-out; }
.delay-100 { animation-delay: .1s; }
.delay-200 { animation-delay: .2s; }
.custom-scrollbar::-webkit-scrollbar { height:6px; width:6px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background:#e2e8f0; border-radius:10px; }
</style>